import Head from 'next/head';

import { PageWrapper, TextIntro, FaqQuestion } from '@components';
import { Accordion, Box, Flex, Heading, Img, Text } from '@chakra-ui/react';
const SIGN_UP_CONTENT = [
  {
    src: 'sign-up-instructions-01',
    alt: 'instructions-image-1',
    text: '1. To start, click the sign up button to begin.',
  },
  {
    src: 'sign-up-instructions-02',
    alt: 'instructions-image-2',
    text: "2. Enter your details. If you are a parent or a guardian, do not enter your children's details here. This login is for a parent/guardian, who can manage student accounts and payment settings.",
  },
  {
    src: 'sign-up-instructions-03',
    alt: 'instructions-image-3',
    text: '3. We will send a verification email to you. Enter the 6 digit number you received here.',
  },
  {
    src: 'sign-up-instructions-04',
    alt: 'instructions-image-4',
    text: "4. Choose how many students you'd like to sign up. Each student account is unique, with their own progression, report card, characters etc.",
  },
  {
    src: 'sign-up-instructions-05',
    alt: 'instructions-image-5',
    text: '5. Enter your card details to begin your subscription. We do not handle or store any of your payment information - this part of the process is handled by Stripe, a professional payment company.',
  },
  {
    src: 'sign-up-instructions-06',
    alt: 'instructions-image-6',
    text: "6. The final step of the setup process is setting the individual accounts of each student or child that you would like to use Valearnis. The username can be anything as long as it's unique - this will be used by your children/students to log in to Valearnis. You can either fill this section in yourself, or have your students/children fill it out instead.",
  },
];

const FAQ_QUESTIONS = [
  {
    title: 'Account and Registration',
    questions: [
      {
        question: 'How does the sign up process work?',
        answer: (
          <>
            {SIGN_UP_CONTENT.map((item) => (
              <Box key={item.src} mb={'3rem'}>
                <Text>{item.text}</Text>
                <Img
                  src={`https://cdn.valearnis.com/vw/img/${item.src}.png`}
                  alt={item.alt}
                  htmlWidth={'580px'}
                  htmlHeight={'480px'}
                />
              </Box>
            ))}
          </>
        ),
      },
      {
        question: 'I signed up but I cannot view any content, now what?',
        answer: (
          <Text>
            You're still on a parent/guardian account, please log into a student
            account to view and complete lessons. Parent/guardian accounts can
            only manage students, change billing settings, etc. The app is meant
            to be used by students under the student account that you've created
            during the sign up process.
          </Text>
        ),
      },
      {
        question: 'I have two different login details, who do I sign in as?',
        answer: (
          <>
            <Text>
              When first signing up with Valearnis, you are creating a{' '}
              <strong>parent/guardian</strong> account, using an email address
              and password to login. Once you've paid the subscription fee, you
              are directed to a page where you setup a{' '}
              <strong>student account</strong> as well, which uses a username
              instead of an email, to protect the privacy of students. The
              details of this second account are to be given by a{' '}
              <strong>parent/guardian</strong> to their{' '}
              <strong>student/child</strong>.
            </Text>
            <Text>
              Which details you choose to login with depends on want you want to
              do. If you are a <strong>parent/guardian</strong> then you can
              sign in with your email address and password, where you can manage
              your student account(s) under your subscription, change your
              billing details, and cancel or restart your subscription. This
              account type cannot complete lessons or level up, as it's just an
              account for a parent to manage things.
            </Text>
            <Text>
              If you are a <strong>student</strong>, then you login with the{' '}
              <strong>student account details</strong> (username and password).
              Once logged into this account, you can begin your Valearnis
              journey, completing lessons, levelling up your character, checking
              your reports page, etc.
            </Text>
          </>
        ),
      },
    ],
  },
  {
    title: 'Content and Courses',
    questions: [
      {
        question: 'Who is Valearnis for?',
        answer: (
          <Text>
            Valearnis is mainly for parents and guardians seeking to provide
            their students/children with a tutoring alternative, grade improve,
            and/or homeschooling enhancement. The sign up process is for a
            parent/guardian, but the platform itself is for students. Our
            curriculum currently supports Year 7-9 Australian curriculum, with
            other year levels and curricula currently in the works
          </Text>
        ),
      },
      {
        question: 'What do you teach?',
        answer: (
          <Text>
            Our Australian curriculum covers Maths, English, Science, and HASS
            (Humanities and Social Sciences) for Year 7-9. We are adding more
            lessons every week and have plans for more year levels and
            curricula.
          </Text>
        ),
      },
      {
        question: 'How many times should a lesson be completed?',
        answer: (
          <Text>
            Ideally, you should complete a lesson twice in order to get the full
            benefits of the theory. After two attempts, your score doesn't
            affect your reports page, however.
          </Text>
        ),
      },
    ],
  },
  {
    title: 'Features and Functionality',
    questions: [
      {
        question: 'Are there videos?',
        answer: (
          <Text>
            Yes, video lessons are being worked on as we speak and will be
            rolled out shortly.
          </Text>
        ),
      },
      {
        question: 'Are there more features coming?',
        answer: (
          <Text>
            Of course! We're a small team, and Valearnis has just launched. We
            have a long way to go on our journey. With your support, we'll be
            adding in full videos, personalised learning AI, more question
            types, more lessons, factions/houses, and much more!
          </Text>
        ),
      },
      {
        question:
          'I am a student, how do I view my character and experience points? How do I view my reports page?',
        answer: (
          <Text>
            Please click the display picture on the top right of the screen.
            Once you click this, you can view your character profile, reports,
            settings, etc.
          </Text>
        ),
      },
      {
        question:
          'Do you have an Android or iOS app? I cannot find Valearnis in an app store',
        answer: (
          <>
            <Text>
              Right now, Valearnis is only available as a 'web app', meaning to
              access it, you must use a web browser (such as Google Chrome,
              Firefox, etc). However, this web app is able to be used on any
              modern device, such as a laptop, PC, iPhone, iPad, Samsung tablet,
              Android phone, etc.
            </Text>
            <Text>
              {' '}
              We recommend using Google Chrome, Firefox, or Microsoft Edge for
              the best experience, but you can use whichever modern browser you
              are most comfortable with.
            </Text>
            <Text>
              Once subscribed, you can bookmark the Valearnis dashboard to make
              access easier (ctrl+D or ⌘+D in most browsers).
            </Text>
          </>
        ),
      },
    ],
  },
  {
    title: 'Pricing and Payment',
    questions: [
      {
        question: 'What does it cost?',
        answer: (
          <Text>
            Valearnis costs a base-price of $15.99 AUD/month per student, but
            this price reduces the more students you're signing up under a
            single subscription. There are discount codes available as well,
            such as EARLYBIRD40 (yay, you read the FAQ and got 40% off!).
          </Text>
        ),
      },
      {
        question: 'Can I cancel my subscription at any time?',
        answer: (
          <Text>
            Yes, you may cancel at any time from the Parent/Guardian dashboard.
          </Text>
        ),
      },
    ],
  },
  {
    title: 'Technical Issues',
    questions: [
      {
        question: "Something isn't working, I think there's a bug",
        answer: (
          <Text>
            Please use the 'contact us' form to get in touch so we can help you,
            or alternatively, please contact us at hello@valearnis.com
          </Text>
        ),
      },
    ],
  },
  {
    title: 'Support and Contact',
    questions: [
      {
        question:
          'What is ManezCo? I saw a payment made out to ManezCo Pty. Ltd. on my statement',
        answer: (
          <Text>
            ManezCo Pty. Ltd. is the company that created Valearnis. We're an
            Australian proprietary limited company registered with the
            Australian Securities and Investments Comission (ASIC). Our ABN is
            41613640565.
          </Text>
        ),
      },
      {
        question: 'How can I contact customer support? ',
        answer: (
          <Text>
            Please email pigeon@valearnis.com explaining your issue, along with
            your account information.
          </Text>
        ),
      },
      {
        question: 'Where can I find additional resources for help? ',
        answer: (
          <Text>
            Our social media pages on Facebook and Instagram provide some
            additional information, but don’t hesitate to get in contact with us
            directly!
          </Text>
        ),
      },
    ],
  },
];

const FaqSection: React.FC<{
  section: (typeof FAQ_QUESTIONS)[number];
}> = (props) => {
  // function that defines the faq section

  return (
    <Box>
      <Heading as={'h5'} fontSize={'xl'} marginBottom={8} marginTop={8}>
        {props.section.title}
      </Heading>
      <Box>
        {props.section.questions.map((question) => (
          <FaqQuestion
            key={question.question}
            heading={
              <Heading
                as={'h2'}
                my={2}
                fontSize={'lg'}
                fontWeight={600}
                letterSpacing={'0.5px'}
              >
                {question.question}
              </Heading>
            }
            content={<Flex flexFlow={'column'}>{question.answer}</Flex>}
          />
        ))}
      </Box>
    </Box>
  );
};

const FaqPage: React.FC = () => {
  return (
    <>
      <Head>
        <title>Frequently Asked Questions | Valearnis</title>
        <meta
          name="description"
          content="The most frequently asked questions about us and our app."
        />
      </Head>
      <PageWrapper>
        <TextIntro heading={'Frequently Asked Questions'} />
        <Accordion mt={4} allowToggle>
          {FAQ_QUESTIONS.map((section) => {
            return <FaqSection key={section.title} section={section} />;
          })}
        </Accordion>
      </PageWrapper>
    </>
  );
};

export default FaqPage;
